import { Component, Inject, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { classeBadgeVariavel, isVariavelParametro } from '@shared/components/formula/utils/formula-badge.util';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { VariaveisDisponiveisDialogData } from './variaveis-disponiveis-dialog-data';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';

export { VariaveisDisponiveisDialogData } from './variaveis-disponiveis-dialog-data';
export { VariaveisCursorPayload } from './variaveis-cursor-payload';

@Component({
    selector: 'app-variaveis-disponiveis-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatTableModule,
        MatFormFieldModule,
        MatInputModule,
        EmptyStateComponent,
    ],
    templateUrl: './variaveis-disponiveis-dialog.component.html',
    styleUrl: './variaveis-disponiveis-dialog.component.css',
})
export class VariaveisDisponiveisDialogComponent {
    public searchControl = new FormControl('');
    public variavelSelecionada: VariavelStruct | null = null;
    public readonly colunas = ['variavel', 'observacao'];

    public isNarrow = window.innerWidth <= 800;

    @HostListener('window:resize')
    onResize(): void {
        this.isNarrow = window.innerWidth <= 800;
    }

    public get variavelFiltradas(): VariavelStruct[] {
        const texto = (this.searchControl.value ?? '').toLowerCase().trim();
        return this.data.variaveis
            .filter(v => {
                const chave = this.variavelChave(v).toLowerCase();
                const matchTexto = !texto ||
                    chave.includes(texto) ||
                    (v.pathTagXml ?? '').toLowerCase().includes(texto) ||
                    v.descricao.toLowerCase().includes(texto) ||
                    (v.nome ?? '').toLowerCase().includes(texto) ||
                    (v.observacao ?? '').toLowerCase().includes(texto);
                return matchTexto;
            });
    }

    constructor(
        public dialogRef: MatDialogRef<VariaveisDisponiveisDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: VariaveisDisponiveisDialogData,
    ) {}

    public readonly classeBadgeVariavel = classeBadgeVariavel;
    public readonly isVariavelParametro = isVariavelParametro;

    public selecionarVariavel(v: VariavelStruct): void {
        this.variavelSelecionada = v;
    }

    public inserirVariavel(): void {
        if (!this.variavelSelecionada) return;

        if (this.data.onInsert) {
            this.data.onInsert(this.variavelSelecionada);
            this.dialogRef.close();
            return;
        }

        if (!this.data.targetControl) {
            this.dialogRef.close();
            return;
        }

        const tag = `{${this.variavelChave(this.variavelSelecionada)}}`;
        const atual: string = this.data.targetControl.value ?? '';

        const start = this.data.cursorStart;
        if (start != null && start >= 0 && start <= atual.length) {
            // Insere no ponto onde o cursor estava, substituindo eventual seleção.
            const end = this.data.cursorEnd != null && this.data.cursorEnd >= start
                ? this.data.cursorEnd
                : start;
            const novo = atual.slice(0, start) + tag + atual.slice(end);
            this.data.targetControl.setValue(novo);
        } else {
            // Fallback: anexa ao final.
            this.data.targetControl.setValue(atual + (atual ? ' ' : '') + tag);
        }

        this.data.targetControl.markAsDirty();
        this.dialogRef.close();
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    public variavelChave(variavel: VariavelStruct): string {
        return variavel.chave || variavel.tagXml || variavel.nome;
    }
}
